import React, { Fragment, useEffect, useMemo, useState } from "react";
import "./Styles.css";
import HeaderToMainPage from "../../Components/Universal/Header/Index";
import Footer from "../../Components/Universal/Footer/Index";

const LAST_UPDATED = "September 8, 2026";
const EFFECTIVE_DATE = "September 8, 2026";

const sections = [
    { id: "overview", number: "01", label: "Overview" },
    { id: "scope", number: "02", label: "Scope" },
    { id: "information-we-collect", number: "03", label: "Information We Collect" },
    { id: "how-we-use-information", number: "04", label: "How We Use Information" },
    { id: "gaming-and-compliance", number: "05", label: "Gaming & Compliance Data" },
    { id: "payments", number: "06", label: "Payments" },
    { id: "geolocation", number: "07", label: "Geolocation" },
    { id: "sms", number: "08", label: "SMS & Mobile Privacy" },
    { id: "sharing", number: "09", label: "Sharing & Disclosure" },
    { id: "cookies", number: "10", label: "Cookies & Analytics" },
    { id: "retention", number: "11", label: "Retention" },
    { id: "security", number: "12", label: "Security" },
    { id: "rights", number: "13", label: "Your Privacy Rights" },
    { id: "children", number: "14", label: "Children & Age Restrictions" },
    { id: "international", number: "15", label: "International Transfers" },
    { id: "third-parties", number: "16", label: "Third-Party Services" },
    { id: "changes", number: "17", label: "Changes to This Policy" },
    { id: "contact", number: "18", label: "Contact Us" },
];

function LegalIcon({ type = "shield" }) {
    const icons = {
        shield: (
            <>
                <path d="M12 3 5 6v5c0 4.7 2.8 8.9 7 10 4.2-1.1 7-5.3 7-10V6l-7-3Z" />
                <path d="m9.4 12 1.7 1.7 3.8-4" />
            </>
        ),
        lock: (
            <>
                <rect x="5" y="10" width="14" height="10" rx="2" />
                <path d="M8 10V7a4 4 0 0 1 8 0v3" />
            </>
        ),
        phone: (
            <>
                <path d="M7 3h4l1 5-2.5 1.5a15 15 0 0 0 5 5L16 12l5 1v4c0 2.2-1.8 4-4 4C9.3 21 3 14.7 3 7c0-2.2 1.8-4 4-4Z" />
            </>
        ),
        location: (
            <>
                <path d="M12 21s6-5.1 6-11a6 6 0 1 0-12 0c0 5.9 6 11 6 11Z" />
                <circle cx="12" cy="10" r="2" />
            </>
        ),
        database: (
            <>
                <ellipse cx="12" cy="5" rx="7" ry="3" />
                <path d="M5 5v6c0 1.7 3.1 3 7 3s7-1.3 7-3V5" />
                <path d="M5 11v6c0 1.7 3.1 3 7 3s7-1.3 7-3v-6" />
            </>
        ),
    };

    return (
        <svg
            className="privacy-icon"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.7"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
        >
            {icons[type]}
        </svg>
    );
}

function PolicySection({
    id,
    number,
    eyebrow,
    title,
    intro,
    children,
    accent = "cyan",
}) {
    return (
        <section
            id={id}
            className={`privacy-section privacy-section--${accent} privacy-reveal`}
        >
            <div className="privacy-section__number" aria-hidden="true">
                {number}
            </div>

            <div className="privacy-section__content">
                <div className="privacy-section__eyebrow">{eyebrow}</div>
                <h2>{title}</h2>
                {intro && <p className="privacy-section__lead">{intro}</p>}
                {children}
            </div>
        </section>
    );
}

function BulletList({ children }) {
    return <ul className="privacy-list">{children}</ul>;
}

function DataCard({ title, children, icon = "database" }) {
    return (
        <article className="privacy-data-card">
            <div className="privacy-data-card__icon">
                <LegalIcon type={icon} />
            </div>
            <div>
                <h3>{title}</h3>
                <p>{children}</p>
            </div>
        </article>
    );
}

export default function PrivacyPolicyPage() {
    const [activeSection, setActiveSection] = useState("overview");
    const [scrollProgress, setScrollProgress] = useState(0);

    const sectionIds = useMemo(() => sections.map((section) => section.id), []);

    useEffect(() => {
        const onScroll = () => {
            const doc = document.documentElement;
            const maxScroll = doc.scrollHeight - window.innerHeight;

            setScrollProgress(
                maxScroll > 0 ? Math.min((window.scrollY / maxScroll) * 100, 100) : 0
            );

            let current = sectionIds[0];

            for (const id of sectionIds) {
                const el = document.getElementById(id);

                if (el) {
                    const rect = el.getBoundingClientRect();

                    if (rect.top <= 180) {
                        current = id;
                    }
                }
            }

            setActiveSection(current);
        };

        const revealObserver = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        entry.target.classList.add("privacy-reveal--visible");
                    }
                });
            },
            {
                threshold: 0.08,
                rootMargin: "0px 0px -40px 0px",
            }
        );

        document
            .querySelectorAll(".privacy-reveal")
            .forEach((element) => revealObserver.observe(element));

        window.addEventListener("scroll", onScroll, { passive: true });
        onScroll();

        return () => {
            window.removeEventListener("scroll", onScroll);
            revealObserver.disconnect();
        };
    }, [sectionIds]);

    const scrollToSection = (id) => {
        document.getElementById(id)?.scrollIntoView({
            behavior: "smooth",
            block: "start",
        });
    };

    return (
        <Fragment>
            <HeaderToMainPage />
            <main className="privacy-page">
                <div
                    className="privacy-scroll-progress"
                    style={{ width: `${scrollProgress}%` }}
                />

                <div className="privacy-background" aria-hidden="true">
                    <div className="privacy-background__grid" />
                    <div className="privacy-orb privacy-orb--one" />
                    <div className="privacy-orb privacy-orb--two" />
                    <div className="privacy-orb privacy-orb--three" />
                    <div className="privacy-noise" />
                </div>

                {/* HERO */}
                <header className="privacy-hero">
                    <div className="privacy-shell">
                        <div className="privacy-hero__topbar">
                            <a href="/" className="privacy-brand">
                                <span className="privacy-brand__mark">
                                    <span />
                                    <span />
                                </span>

                                <span className="privacy-brand__text">ETHOSINO</span>
                            </a>

                            <a href="/" className="privacy-back-link">
                                Back to Ethosino
                                <span>↗</span>
                            </a>
                        </div>

                        <div className="privacy-hero__layout">
                            <div className="privacy-hero__copy">
                                <div className="privacy-kicker">
                                    <span className="privacy-kicker__dot" />
                                    LEGAL / PRIVACY
                                </div>

                                <h1>
                                    Privacy should be
                                    <span> engineered in.</span>
                                </h1>

                                <p className="privacy-hero__description">
                                    This Privacy Policy explains how Ethosino collects, uses,
                                    protects, retains, and shares information when people interact
                                    with our websites, applications, gaming infrastructure,
                                    operator environments, communications, and related services.
                                </p>

                                <div className="privacy-hero__meta">
                                    <div>
                                        <span>Effective</span>
                                        <strong>{EFFECTIVE_DATE}</strong>
                                    </div>

                                    <div>
                                        <span>Last updated</span>
                                        <strong>{LAST_UPDATED}</strong>
                                    </div>

                                    <div>
                                        <span>Version</span>
                                        <strong>1.0</strong>
                                    </div>
                                </div>
                            </div>

                            <div className="privacy-hero__visual">
                                <div className="privacy-terminal privacy-reveal">
                                    <div className="privacy-terminal__header">
                                        <div className="privacy-terminal__dots">
                                            <span />
                                            <span />
                                            <span />
                                        </div>

                                        <span>privacy.config</span>

                                        <span className="privacy-terminal__status">
                                            <i />
                                            ACTIVE
                                        </span>
                                    </div>

                                    <div className="privacy-terminal__body">
                                        <div className="privacy-code-line">
                                            <span className="privacy-code-line__index">01</span>
                                            <span className="privacy-code-cyan">privacy</span>
                                            <span className="privacy-code-muted">:</span>
                                            <span className="privacy-code-green"> protected</span>
                                        </div>

                                        <div className="privacy-code-line">
                                            <span className="privacy-code-line__index">02</span>
                                            <span className="privacy-code-cyan">sms_consent</span>
                                            <span className="privacy-code-muted">:</span>
                                            <span className="privacy-code-green"> private</span>
                                        </div>

                                        <div className="privacy-code-line">
                                            <span className="privacy-code-line__index">03</span>
                                            <span className="privacy-code-cyan">data_sale</span>
                                            <span className="privacy-code-muted">:</span>
                                            <span className="privacy-code-red"> false</span>
                                        </div>

                                        <div className="privacy-code-line">
                                            <span className="privacy-code-line__index">04</span>
                                            <span className="privacy-code-cyan">security</span>
                                            <span className="privacy-code-muted">:</span>
                                            <span className="privacy-code-purple"> layered</span>
                                        </div>

                                        <div className="privacy-code-line">
                                            <span className="privacy-code-line__index">05</span>
                                            <span className="privacy-code-cyan">control</span>
                                            <span className="privacy-code-muted">:</span>
                                            <span className="privacy-code-green"> user</span>
                                        </div>

                                        <div className="privacy-terminal__divider" />

                                        <div className="privacy-terminal__footer">
                                            <span>ETHOSINO PRIVACY LAYER</span>
                                            <span>● VERIFIED POLICY</span>
                                        </div>
                                    </div>
                                </div>

                                <div className="privacy-floating-card privacy-floating-card--top">
                                    <span className="privacy-floating-card__icon">
                                        <LegalIcon type="shield" />
                                    </span>

                                    <div>
                                        <span>Privacy controls</span>
                                        <strong>Built into the stack</strong>
                                    </div>
                                </div>

                                <div className="privacy-floating-card privacy-floating-card--bottom">
                                    <span className="privacy-floating-card__icon">
                                        <LegalIcon type="lock" />
                                    </span>

                                    <div>
                                        <span>SMS consent data</span>
                                        <strong>Never sold for marketing</strong>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="privacy-hero__statement privacy-reveal">
                            <div>
                                <span>THE CORE PRINCIPLE</span>
                                <strong>Your data should serve the product — not become it.</strong>
                            </div>

                            <p>
                                We collect information necessary to operate, secure, improve,
                                support, and legally administer Ethosino-powered services. We do
                                not treat private customer information as an advertising asset.
                            </p>
                        </div>
                    </div>
                </header>

                {/* MAIN BODY */}
                <div className="privacy-shell privacy-document-layout">
                    <aside className="privacy-toc">
                        <div className="privacy-toc__sticky">
                            <span className="privacy-toc__heading">POLICY INDEX</span>

                            <nav aria-label="Privacy Policy sections">
                                {sections.map((section) => (
                                    <button
                                        key={section.id}
                                        type="button"
                                        onClick={() => scrollToSection(section.id)}
                                        className={
                                            activeSection === section.id
                                                ? "privacy-toc__item privacy-toc__item--active"
                                                : "privacy-toc__item"
                                        }
                                    >
                                        <span>{section.number}</span>
                                        {section.label}
                                    </button>
                                ))}
                            </nav>

                            <div className="privacy-toc__contact">
                                <span>Privacy question?</span>
                                <a href="mailto:privacy@ethosino.com">
                                    privacy@ethosino.com
                                </a>
                            </div>
                        </div>
                    </aside>

                    <article className="privacy-document">
                        <PolicySection
                            id="overview"
                            number="01"
                            eyebrow="PRIVACY AT ETHOSINO"
                            title="Overview"
                            accent="cyan"
                            intro="Ethosino provides technology and infrastructure for digital gaming, entertainment, promotional gaming, operator administration, player experiences, and related services."
                        >
                            <p>
                                This Privacy Policy describes how Ethosino and its applicable
                                affiliates, subsidiaries, service environments, and platform
                                operations ("Ethosino," "we," "us," or "our") collect, use,
                                disclose, retain, and protect personal information.
                            </p>

                            <p>
                                Depending on how you interact with Ethosino, we may act as a
                                business responsible for determining how information is used, or
                                we may process information on behalf of an independent operator
                                using Ethosino technology. Where an independent operator controls
                                a gaming property powered by Ethosino, that operator may maintain
                                its own privacy policy and may independently determine certain
                                purposes for processing your information.
                            </p>

                            <div className="privacy-highlight">
                                <div className="privacy-highlight__icon">
                                    <LegalIcon type="shield" />
                                </div>

                                <div>
                                    <span>Plain-language commitment</span>
                                    <p>
                                        We aim to collect only information reasonably necessary to
                                        provide, protect, administer, improve, or comply with legal
                                        requirements applicable to the services you use.
                                    </p>
                                </div>
                            </div>
                        </PolicySection>

                        <PolicySection
                            id="scope"
                            number="02"
                            eyebrow="WHERE THIS POLICY APPLIES"
                            title="Scope"
                            accent="purple"
                        >
                            <p>This Policy applies to information collected through:</p>

                            <BulletList>
                                <li>ethosino.com and associated Ethosino websites;</li>
                                <li>demo.ethosino.com and demonstration environments;</li>
                                <li>Ethosino-powered gaming or promotional environments;</li>
                                <li>operator and administrative dashboards;</li>
                                <li>account registration and authentication systems;</li>
                                <li>player wallet and transaction systems;</li>
                                <li>customer support and communications;</li>
                                <li>SMS, email, and service notifications;</li>
                                <li>application programming interfaces and integrations;</li>
                                <li>
                                    fraud prevention, geolocation, identity verification, and
                                    compliance systems; and
                                </li>
                                <li>
                                    other services that reference or link to this Privacy Policy.
                                </li>
                            </BulletList>

                            <p>
                                This Policy does not necessarily govern independent third-party
                                websites, payment providers, identity verification providers,
                                game studios, external operators, or other services that maintain
                                their own privacy policies.
                            </p>
                        </PolicySection>

                        <PolicySection
                            id="information-we-collect"
                            number="03"
                            eyebrow="DATA INPUTS"
                            title="Information we collect"
                            accent="cyan"
                            intro="The information we collect depends on the services you use, the operator environment you access, and the actions you take."
                        >
                            <div className="privacy-card-grid">
                                <DataCard title="Account information">
                                    Name, username, email address, telephone number, login
                                    credentials, account identifiers, date of birth, profile
                                    information, and account preferences.
                                </DataCard>

                                <DataCard title="Identity & verification" icon="shield">
                                    Information used for age verification, identity verification,
                                    KYC, fraud prevention, eligibility checks, and other regulatory
                                    or compliance purposes.
                                </DataCard>

                                <DataCard title="Transaction information">
                                    Deposit and withdrawal records, wallet movements, promotional
                                    credits, balances, transaction status, payment method metadata,
                                    and related financial records.
                                </DataCard>

                                <DataCard title="Gaming activity">
                                    Games played, wagers or entries, results, tournament activity,
                                    prizes, bonuses, game session data, wallet movements, fairness
                                    records, timestamps, and related activity.
                                </DataCard>

                                <DataCard title="Technical information">
                                    IP address, browser type, device information, operating system,
                                    user agent, session identifiers, application events, error
                                    information, network information, and security logs.
                                </DataCard>

                                <DataCard title="Location information" icon="location">
                                    Approximate or precise location information when necessary to
                                    determine service availability, jurisdiction, regulatory
                                    eligibility, fraud risk, or compliance with location-based
                                    restrictions.
                                </DataCard>

                                <DataCard title="Communications" icon="phone">
                                    Messages sent to customer support, contact form submissions,
                                    emails, SMS interactions, support tickets, feedback, and other
                                    communications with us.
                                </DataCard>

                                <DataCard title="Analytics information">
                                    Pages visited, feature usage, session duration, interaction
                                    events, referral information, campaign information, and
                                    aggregated product analytics.
                                </DataCard>
                            </div>

                            <h3>Information provided directly by you</h3>

                            <p>
                                We may collect information when you create an account, contact
                                support, participate in a promotion, submit identity documents,
                                enter payment information, request a withdrawal, communicate with
                                an operator, subscribe to communications, or otherwise interact
                                with the Services.
                            </p>

                            <h3>Information collected automatically</h3>

                            <p>
                                When you use the Services, certain technical and usage
                                information may be collected automatically through application
                                logs, cookies, local storage, analytics tools, security systems,
                                fraud detection tools, and similar technologies.
                            </p>

                            <h3>Information received from other sources</h3>

                            <p>
                                We may receive information from operators, identity verification
                                providers, payment processors, game providers, fraud prevention
                                services, analytics providers, geolocation providers,
                                infrastructure providers, referral partners, or other service
                                providers where permitted by law.
                            </p>
                        </PolicySection>

                        <PolicySection
                            id="how-we-use-information"
                            number="04"
                            eyebrow="PURPOSE"
                            title="How we use information"
                            accent="green"
                        >
                            <p>We may use personal information to:</p>

                            <BulletList>
                                <li>create, authenticate, and maintain user accounts;</li>
                                <li>operate Ethosino and operator-powered gaming environments;</li>
                                <li>maintain player wallets and transaction histories;</li>
                                <li>process deposits, withdrawals, prizes, and adjustments;</li>
                                <li>administer bonuses, promotions, rewards, and tournaments;</li>
                                <li>provide games and record game activity;</li>
                                <li>provide customer support;</li>
                                <li>send transactional and service-related communications;</li>
                                <li>verify age, identity, eligibility, or jurisdiction;</li>
                                <li>perform KYC, AML, fraud, sanctions, and risk checks;</li>
                                <li>detect abuse, cheating, account takeover, or manipulation;</li>
                                <li>maintain security and prevent unauthorized access;</li>
                                <li>resolve disputes and investigate suspicious activity;</li>
                                <li>monitor service performance and troubleshoot errors;</li>
                                <li>improve products, features, design, and user experience;</li>
                                <li>develop aggregated or de-identified analytics;</li>
                                <li>comply with applicable laws and regulatory obligations;</li>
                                <li>enforce our agreements and platform rules; and</li>
                                <li>
                                    protect Ethosino, operators, users, partners, and the public.
                                </li>
                            </BulletList>

                            <div className="privacy-quote">
                                <span>OUR APPROACH</span>
                                <blockquote>
                                    Information should be used because the platform needs it — not
                                    simply because it can be collected.
                                </blockquote>
                            </div>
                        </PolicySection>

                        <PolicySection
                            id="gaming-and-compliance"
                            number="05"
                            eyebrow="GAMING INFRASTRUCTURE"
                            title="Gaming, fairness, risk & compliance data"
                            accent="purple"
                        >
                            <p>
                                Because Ethosino supports gaming and gaming-adjacent services,
                                certain information may be necessary to maintain fair operation,
                                prevent abuse, verify eligibility, support operator obligations,
                                and document game outcomes.
                            </p>

                            <p>This information may include:</p>

                            <BulletList>
                                <li>game identifiers and session identifiers;</li>
                                <li>entry or wager amounts;</li>
                                <li>game results and payout information;</li>
                                <li>server seed hashes, client seeds, nonces, and fairness data;</li>
                                <li>tournament participation and tournament results;</li>
                                <li>bonus activity and promotional eligibility;</li>
                                <li>account restrictions and risk flags;</li>
                                <li>withdrawal review information;</li>
                                <li>suspected fraud or account abuse indicators;</li>
                                <li>device or IP-risk indicators;</li>
                                <li>self-exclusion or responsible-gaming restrictions where applicable;</li>
                                <li>geographic eligibility information; and</li>
                                <li>records required by an operator or applicable law.</li>
                            </BulletList>

                            <p>
                                Some gaming records may need to be retained for extended periods
                                where necessary to satisfy financial, regulatory, audit,
                                anti-fraud, dispute-resolution, or legal obligations.
                            </p>
                        </PolicySection>

                        <PolicySection
                            id="payments"
                            number="06"
                            eyebrow="TRANSACTIONS"
                            title="Payments and financial information"
                            accent="green"
                        >
                            <p>
                                Payment transactions may be processed by third-party payment
                                processors or financial service providers. Ethosino may receive
                                transaction metadata such as transaction identifiers, payment
                                status, amount, currency, processor responses, limited payment
                                method details, and other information required to reconcile
                                transactions.
                            </p>

                            <div className="privacy-security-row">
                                <div>
                                    <span>01</span>
                                    <strong>Processor-led handling</strong>
                                    <p>
                                        Full payment card information should generally be entered
                                        through the applicable payment processor's secured payment
                                        environment rather than stored directly by Ethosino.
                                    </p>
                                </div>

                                <div>
                                    <span>02</span>
                                    <strong>Transaction records</strong>
                                    <p>
                                        We may retain transaction metadata necessary for account
                                        history, support, reconciliation, fraud detection, dispute
                                        resolution, and compliance.
                                    </p>
                                </div>

                                <div>
                                    <span>03</span>
                                    <strong>Withdrawal reviews</strong>
                                    <p>
                                        Additional identity, eligibility, or anti-fraud information
                                        may be requested before a withdrawal is approved.
                                    </p>
                                </div>
                            </div>
                        </PolicySection>

                        <PolicySection
                            id="geolocation"
                            number="07"
                            eyebrow="JURISDICTION"
                            title="Geolocation information"
                            accent="cyan"
                        >
                            <p>
                                Certain Ethosino-powered services may be restricted based on
                                geographic location. Where necessary, we or an operator may
                                request permission to obtain location information from your
                                device or estimate location using IP address or other technical
                                information.
                            </p>

                            <p>
                                Geolocation information may be used to determine whether a
                                service is available in your jurisdiction, enforce territorial
                                restrictions, prevent fraud, identify VPN or proxy activity,
                                support regulatory compliance, or investigate suspicious
                                activity.
                            </p>

                            <div className="privacy-highlight privacy-highlight--location">
                                <div className="privacy-highlight__icon">
                                    <LegalIcon type="location" />
                                </div>

                                <div>
                                    <span>Location permission</span>
                                    <p>
                                        Where precise device location requires permission, your
                                        browser or device may allow you to approve or deny that
                                        permission. Denying location access may prevent access to
                                        features that require jurisdiction verification.
                                    </p>
                                </div>
                            </div>
                        </PolicySection>

                        <PolicySection
                            id="sms"
                            number="08"
                            eyebrow="A2P / MOBILE MESSAGING"
                            title="SMS messaging & mobile information"
                            accent="sms"
                            intro="Mobile privacy deserves its own rule set."
                        >
                            <div className="privacy-sms-banner">
                                <div className="privacy-sms-banner__icon">
                                    <LegalIcon type="phone" />
                                </div>

                                <div>
                                    <span>SMS PRIVACY COMMITMENT</span>
                                    <h3>
                                        Mobile information and messaging consent are not marketing
                                        inventory.
                                    </h3>
                                </div>
                            </div>

                            <p>
                                If you provide a mobile telephone number and consent to receive
                                SMS or text messages from Ethosino, we may use that number to
                                provide customer-care, account-related, operational,
                                transactional, security, or other messages consistent with the
                                consent you provided.
                            </p>

                            <div className="privacy-sms-rule">
                                <span>IMPORTANT</span>

                                <p>
                                    <strong>
                                        Ethosino does not sell, rent, share, or provide mobile phone
                                        numbers, SMS opt-in information, or messaging consent data to
                                        third parties or affiliates for their marketing or
                                        promotional purposes.
                                    </strong>
                                </p>
                            </div>

                            <p>
                                This restriction applies to information concerning whether you
                                consented to receive SMS communications and the mobile number
                                associated with that consent.
                            </p>

                            <p>
                                We may disclose mobile information to service providers that help
                                us deliver messages or operate the communications infrastructure
                                when necessary to provide the requested service, subject to
                                appropriate contractual or legal restrictions. Such service
                                providers are not authorized to use your mobile information for
                                their own marketing purposes.
                            </p>

                            <h3>Consent to SMS communications</h3>

                            <p>
                                Where SMS consent is requested, consent should be provided
                                voluntarily through the applicable opt-in mechanism. Consent to
                                receive SMS marketing messages is not a condition of purchasing a
                                product or service unless otherwise permitted by applicable law.
                            </p>

                            <h3>Message frequency and carrier charges</h3>

                            <p>
                                Message frequency may vary depending on your interactions with
                                Ethosino and the services you use. Message and data rates may
                                apply depending on your mobile carrier and service plan.
                            </p>

                            <h3>Opting out</h3>

                            <p>
                                You may generally opt out of applicable SMS messages by replying
                                <strong> STOP</strong>. You may reply <strong>HELP</strong> for
                                additional assistance. We may send a final confirmation message
                                acknowledging your opt-out request.
                            </p>

                            <h3>Transactional or security communications</h3>

                            <p>
                                Depending on the service and applicable law, certain essential
                                security or account communications may be delivered through
                                channels necessary to protect your account or complete a request.
                                Where required, separate consent mechanisms will be used.
                            </p>
                        </PolicySection>

                        <PolicySection
                            id="sharing"
                            number="09"
                            eyebrow="DISCLOSURE"
                            title="How information may be shared"
                            accent="purple"
                        >
                            <p>
                                We do not sell personal information in exchange for money as a
                                core part of Ethosino's business model.
                            </p>

                            <p>
                                Information may be disclosed to the following categories of
                                recipients when reasonably necessary:
                            </p>

                            <BulletList>
                                <li>
                                    <strong>Operators.</strong> If you use an Ethosino-powered
                                    operator environment, information may be available to the
                                    operator responsible for that property.
                                </li>

                                <li>
                                    <strong>Infrastructure providers.</strong> Hosting, database,
                                    cloud infrastructure, monitoring, storage, and security
                                    providers.
                                </li>

                                <li>
                                    <strong>Payment providers.</strong> Companies used to process,
                                    authorize, settle, reconcile, or investigate financial
                                    transactions.
                                </li>

                                <li>
                                    <strong>Identity and compliance providers.</strong> Services
                                    used for identity verification, KYC, AML, sanctions screening,
                                    fraud detection, age verification, or eligibility checks.
                                </li>

                                <li>
                                    <strong>Game and content providers.</strong> Third-party game,
                                    sports, skill-gaming, or content providers when necessary to
                                    provide the requested experience.
                                </li>

                                <li>
                                    <strong>Analytics and support providers.</strong> Services used
                                    to understand product performance or provide customer service.
                                </li>

                                <li>
                                    <strong>Professional advisors.</strong> Attorneys, accountants,
                                    auditors, insurers, consultants, or similar professional
                                    advisers.
                                </li>

                                <li>
                                    <strong>Legal and regulatory authorities.</strong> Where
                                    required by subpoena, court order, law, regulation, lawful
                                    government request, or regulatory obligation.
                                </li>

                                <li>
                                    <strong>Corporate transactions.</strong> Information may be
                                    transferred as part of a merger, financing, acquisition,
                                    restructuring, asset transfer, insolvency, or similar
                                    transaction, subject to applicable legal requirements.
                                </li>
                            </BulletList>

                            <div className="privacy-no-sale">
                                <span className="privacy-no-sale__big">NO</span>

                                <div>
                                    <strong>SMS consent resale.</strong>
                                    <p>
                                        Mobile numbers and SMS consent records are not shared with
                                        third parties or affiliates for their independent marketing
                                        or promotional use.
                                    </p>
                                </div>
                            </div>
                        </PolicySection>

                        <PolicySection
                            id="cookies"
                            number="10"
                            eyebrow="BROWSER TECHNOLOGY"
                            title="Cookies, local storage & analytics"
                            accent="cyan"
                        >
                            <p>
                                Ethosino may use cookies, browser storage, pixels, SDKs,
                                application events, log files, and related technologies to
                                maintain sessions, remember preferences, protect accounts,
                                measure performance, understand product usage, troubleshoot
                                errors, and improve the Services.
                            </p>

                            <h3>Examples may include</h3>

                            <BulletList>
                                <li>authentication and session cookies;</li>
                                <li>security and fraud-prevention identifiers;</li>
                                <li>preference storage;</li>
                                <li>analytics and product usage events;</li>
                                <li>referral or campaign attribution parameters;</li>
                                <li>error and performance telemetry; and</li>
                                <li>feature configuration information.</li>
                            </BulletList>

                            <p>
                                Your browser may allow you to block or delete cookies. Some
                                features may not function properly if cookies or similar storage
                                mechanisms are disabled.
                            </p>
                        </PolicySection>

                        <PolicySection
                            id="retention"
                            number="11"
                            eyebrow="DATA LIFECYCLE"
                            title="Data retention"
                            accent="green"
                        >
                            <p>
                                We retain personal information for as long as reasonably
                                necessary to provide the Services, maintain legitimate business
                                records, comply with legal or regulatory requirements, resolve
                                disputes, prevent fraud, enforce agreements, and protect our
                                users and platform.
                            </p>

                            <div className="privacy-timeline">
                                <div>
                                    <span>ACTIVE</span>
                                    <strong>Account lifetime</strong>
                                    <p>
                                        Information necessary to operate an active account is
                                        generally retained while the account remains in use.
                                    </p>
                                </div>

                                <div>
                                    <span>REQUIRED</span>
                                    <strong>Compliance records</strong>
                                    <p>
                                        Financial, gaming, identity, audit, and compliance records may
                                        be retained longer where legally or operationally required.
                                    </p>
                                </div>

                                <div>
                                    <span>EXPIRY</span>
                                    <strong>Deletion or de-identification</strong>
                                    <p>
                                        When information is no longer reasonably necessary, it may be
                                        deleted, anonymized, aggregated, or otherwise de-identified.
                                    </p>
                                </div>
                            </div>

                            <p>
                                Backup systems may retain copies for a limited period after
                                deletion from active systems.
                            </p>
                        </PolicySection>

                        <PolicySection
                            id="security"
                            number="12"
                            eyebrow="PROTECTION"
                            title="Information security"
                            accent="purple"
                        >
                            <p>
                                Ethosino uses administrative, technical, and organizational
                                safeguards intended to protect information against unauthorized
                                access, destruction, alteration, disclosure, or misuse.
                            </p>

                            <div className="privacy-card-grid privacy-card-grid--security">
                                <DataCard title="Access controls" icon="lock">
                                    Administrative and system access may be restricted based on
                                    role, environment, and operational need.
                                </DataCard>

                                <DataCard title="Logging & auditability" icon="database">
                                    Security, operator, transaction, and administrative actions may
                                    be logged for review, troubleshooting, compliance, and fraud
                                    prevention.
                                </DataCard>

                                <DataCard title="Infrastructure security" icon="shield">
                                    We use technical safeguards and infrastructure controls designed
                                    to reduce unauthorized access to production systems.
                                </DataCard>

                                <DataCard title="Incident response" icon="shield">
                                    Potential security incidents may be investigated and addressed
                                    according to their nature, severity, and applicable legal
                                    obligations.
                                </DataCard>
                            </div>

                            <p>
                                No internet transmission, database, application, or storage
                                system can be guaranteed to be completely secure. Users are also
                                responsible for protecting their credentials and maintaining the
                                security of devices used to access the Services.
                            </p>
                        </PolicySection>

                        <PolicySection
                            id="rights"
                            number="13"
                            eyebrow="CONTROL"
                            title="Your privacy rights"
                            accent="cyan"
                        >
                            <p>
                                Depending on where you reside, applicable privacy laws may provide
                                rights concerning your personal information. These rights may
                                include the ability to:
                            </p>

                            <BulletList>
                                <li>request access to personal information;</li>
                                <li>request correction of inaccurate information;</li>
                                <li>request deletion of certain information;</li>
                                <li>request a portable copy of certain information;</li>
                                <li>object to or restrict certain processing;</li>
                                <li>withdraw consent where processing is based on consent;</li>
                                <li>opt out of certain marketing communications;</li>
                                <li>
                                    appeal certain decisions regarding a privacy request where
                                    applicable; and
                                </li>
                                <li>
                                    exercise other rights available under applicable law.
                                </li>
                            </BulletList>

                            <p>
                                Some requests may be limited where retention is required for
                                legal, regulatory, anti-fraud, financial, safety, contractual, or
                                other legitimate purposes.
                            </p>

                            <p>
                                We may need to verify your identity before fulfilling certain
                                requests.
                            </p>

                            <div className="privacy-action-card">
                                <div>
                                    <span>PRIVACY REQUEST</span>
                                    <h3>Want to exercise a privacy right?</h3>
                                    <p>
                                        Contact us and include enough information for us to locate and
                                        verify the applicable account or records.
                                    </p>
                                </div>

                                <a href="mailto:privacy@ethosino.com">
                                    privacy@ethosino.com
                                    <span>↗</span>
                                </a>
                            </div>
                        </PolicySection>

                        <PolicySection
                            id="children"
                            number="14"
                            eyebrow="AGE RESTRICTIONS"
                            title="Children and age-restricted services"
                            accent="green"
                        >
                            <p>
                                Ethosino-powered gaming services are not intended for children.
                                Where a service is subject to minimum age requirements, users
                                must satisfy the applicable legal or operator-defined age
                                requirement before participating.
                            </p>

                            <p>
                                We do not knowingly permit children to participate in
                                age-restricted gaming activity. If we learn that personal
                                information from a child has been collected in circumstances
                                where it should not have been, we may take reasonable steps to
                                delete or restrict that information.
                            </p>

                            <p>
                                Parents or legal guardians who believe a child has improperly
                                submitted personal information may contact us at
                                privacy@ethosino.com.
                            </p>
                        </PolicySection>

                        <PolicySection
                            id="international"
                            number="15"
                            eyebrow="DATA LOCATION"
                            title="International processing and transfers"
                            accent="purple"
                        >
                            <p>
                                Ethosino, its operators, infrastructure providers, and service
                                providers may process information in jurisdictions other than the
                                jurisdiction in which you reside.
                            </p>

                            <p>
                                Where required by applicable law, we may use contractual,
                                organizational, technical, or other safeguards designed to
                                protect information transferred across borders.
                            </p>

                            <p>
                                Privacy and data protection laws may vary between jurisdictions.
                                By using the Services, your information may be processed in
                                locations where Ethosino or its providers maintain systems,
                                personnel, or infrastructure, subject to applicable legal
                                requirements.
                            </p>
                        </PolicySection>

                        <PolicySection
                            id="third-parties"
                            number="16"
                            eyebrow="CONNECTED SERVICES"
                            title="Third-party services and integrations"
                            accent="cyan"
                        >
                            <p>
                                Ethosino may integrate with third-party game studios, payment
                                processors, communications providers, analytics tools, cloud
                                infrastructure, identity verification providers, geolocation
                                providers, customer-support systems, content providers, and other
                                services.
                            </p>

                            <p>
                                These third parties may process information under their own
                                privacy policies and contractual obligations. Ethosino does not
                                control every independent third party's privacy practices.
                            </p>

                            <p>
                                We encourage users to review the policies of external services
                                when interacting directly with them.
                            </p>

                            <div className="privacy-integrations">
                                <div>
                                    <span>PAYMENTS</span>
                                </div>
                                <div>
                                    <span>GAME CONTENT</span>
                                </div>
                                <div>
                                    <span>KYC / AML</span>
                                </div>
                                <div>
                                    <span>MESSAGING</span>
                                </div>
                                <div>
                                    <span>ANALYTICS</span>
                                </div>
                                <div>
                                    <span>INFRASTRUCTURE</span>
                                </div>
                            </div>
                        </PolicySection>

                        <PolicySection
                            id="changes"
                            number="17"
                            eyebrow="POLICY VERSIONING"
                            title="Changes to this Privacy Policy"
                            accent="purple"
                        >
                            <p>
                                We may update this Privacy Policy from time to time to reflect
                                changes in our services, technology, business practices,
                                regulatory obligations, or applicable law.
                            </p>

                            <p>
                                When we update this Policy, we will revise the "Last Updated"
                                date shown above. If a change is material, we may provide
                                additional notice where required or appropriate.
                            </p>

                            <p>
                                Your continued use of the Services after an updated Policy takes
                                effect is subject to the updated Policy, except where applicable
                                law requires additional consent.
                            </p>
                        </PolicySection>

                        <PolicySection
                            id="contact"
                            number="18"
                            eyebrow="CONTACT"
                            title="Contact Ethosino"
                            accent="green"
                        >
                            <p>
                                Questions about this Privacy Policy, personal information, SMS
                                privacy, or privacy requests may be directed to:
                            </p>

                            <div className="privacy-contact-panel">
                                <div className="privacy-contact-panel__brand">
                                    <span className="privacy-brand__mark">
                                        <span />
                                        <span />
                                    </span>

                                    <div>
                                        <strong>ETHOSINO</strong>
                                        <span>Privacy & Data Protection</span>
                                    </div>
                                </div>

                                <div className="privacy-contact-panel__details">
                                    <div>
                                        <span>Email</span>
                                        <a href="mailto:privacy@ethosino.com">
                                            privacy@ethosino.com
                                        </a>
                                    </div>

                                    <div>
                                        <span>General support</span>
                                        <a href="mailto:support@ethosino.com">
                                            support@ethosino.com
                                        </a>
                                    </div>

                                    <div>
                                        <span>Website</span>
                                        <a
                                            href="https://ethosino.com"
                                            target="_blank"
                                            rel="noreferrer"
                                        >
                                            ethosino.com
                                        </a>
                                    </div>
                                </div>
                            </div>

                            <div className="privacy-company-placeholder">
                                <span>ACTION REQUIRED BEFORE PUBLISHING</span>

                                <p>
                                    Replace this notice with Ethosino's official legal entity name,
                                    mailing address, and any legally required privacy
                                    representative information.
                                </p>

                                <code>
                                    ETHOSINO LEGAL ENTITY NAME
                                    <br />
                                    [STREET ADDRESS]
                                    <br />
                                    [CITY, STATE ZIP]
                                    <br />
                                    [COUNTRY]
                                </code>
                            </div>
                        </PolicySection>

                        <footer className="privacy-document-footer">
                            <div>
                                <span>ETHOSINO</span>
                                <strong>Privacy Policy</strong>
                            </div>

                            <p>
                                Effective {EFFECTIVE_DATE} · Last updated {LAST_UPDATED}
                            </p>
                        </footer>
                    </article>
                </div>
            </main>
            <Footer />
        </Fragment>
    );
}